import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { attorneyAPI, messageAPI, getErrorKey } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { translatePA } from '../utils/practiceAreaUtils';
import PhoneInput from '../components/common/PhoneInput';

const SERVER_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');

const useWindowWidth = () => {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return w;
};

const ContactAttorney = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isClient } = useAuth();
  const { t, language } = useLanguage();
  const tf = (key, vars) => Object.entries(vars || {}).reduce((str, [k, v]) => str.replace(`{${k}}`, v), t(key));
  const isMobile = useWindowWidth() < 768;

  const [attorney, setAttorney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    situation: '',
    phonePreferred: false,
    phoneNumber: '',
    preferredTime: [],
  });

  useEffect(() => {
    attorneyAPI.getById(id)
      .then(res => setAttorney(res.data))
      .catch(() => setError(t('errors.attorneyNotFound')))
      .finally(() => setLoading(false));

    // Restore saved form data if coming back from registration
    const saved = localStorage.getItem('pendingMessage');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.attorneyId === Number(id)) {
          setForm({ situation: parsed.situation || '', phonePreferred: parsed.phonePreferred || false, phoneNumber: parsed.phoneNumber || '', preferredTime: parsed.preferredTime || [] });
        }
      } catch {}
    }
  }, [id]);

  const toggleTime = (t) => {
    setForm(f => ({
      ...f,
      preferredTime: f.preferredTime.includes(t) ? f.preferredTime.filter(x => x !== t) : [...f.preferredTime, t],
    }));
  };

  const handleSend = async () => {
    if (!form.situation.trim()) { setError(t('errors.describeSituation')); return; }
    if (form.phonePreferred && !form.phoneNumber.trim()) { setError(t('errors.enterPhone')); return; }
    setError('');

    if (!user || !isClient) {
      // Save form data and redirect to register
      localStorage.setItem('pendingMessage', JSON.stringify({
        attorneyId: Number(id),
        situation: form.situation,
        phonePreferred: form.phonePreferred,
        phoneNumber: form.phoneNumber,
        preferredTime: form.preferredTime,
      }));
      navigate('/register', { state: { userType: 'client', returnTo: `/contact/${id}` } });
      return;
    }

    setSending(true);
    try {
      await messageAPI.send({
        attorneyId: Number(id),
        situation: form.situation,
        phonePreferred: form.phonePreferred,
        phoneNumber: form.phoneNumber,
        preferredTime: form.preferredTime.join(', '),
      });
      localStorage.removeItem('pendingMessage');
      setSent(true);
    } catch {
      setError(t('errors.sendError'));
    } finally {
      setSending(false);
    }
  };

  // Auto-send if returning from registration with pending message
  useEffect(() => {
    if (!user || !isClient || !attorney) return;
    const saved = localStorage.getItem('pendingMessage');
    if (!saved) return;
    let parsed;
    try { parsed = JSON.parse(saved); } catch { localStorage.removeItem('pendingMessage'); return; }
    if (parsed.attorneyId !== Number(id) || !parsed.situation) return;
    // Clear IMMEDIATELY before the async call — prevents any other user from triggering this
    localStorage.removeItem('pendingMessage');
    messageAPI.send({
      attorneyId: Number(id),
      situation: parsed.situation,
      phonePreferred: parsed.phonePreferred || false,
      phoneNumber: parsed.phoneNumber || '',
      preferredTime: (parsed.preferredTime || []).join(', '),
    }).then(() => setSent(true)).catch(() => {});
  }, [user, isClient, attorney, id]);

  const TIME_OPTIONS = [
    { key: 'morning', label: t('contact.morning') },
    { key: 'afternoon', label: t('contact.afternoon') },
    { key: 'evening', label: t('contact.evening') },
  ];

  if (loading) return <div style={s.center}>{t('contact.loading')}</div>;
  if (!attorney) return <div style={s.center}>{error || t('contact.notFound')}</div>;

  if (sent) return (
    <div style={s.page}>
      <div style={s.sentBox}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✓</div>
        <h2 style={{ color: '#1a365d', marginBottom: '0.75rem' }}>{t('contact.sentTitle')}</h2>
        <p style={{ color: '#718096', marginBottom: '1.5rem' }}>
          {tf('contact.sentBody', { name: attorney.full_name })}
        </p>
        <button style={s.btnPrimary} onClick={() => { window.location.href = '/'; }}>{t('contact.backHome')}</button>
      </div>
    </div>
  );

  const formBlock = (
        <div style={{ ...s.formBox, ...(isMobile ? { padding: '1.5rem' } : {}) }}>
          <h1 style={s.title}>{t('contact.title')}</h1>
          <p style={s.note}>{t('contact.requiredNote')}</p>

          <p style={s.salutation}>{tf('contact.salutation', { name: attorney.full_name })}</p>

          <div style={s.field}>
            <label style={s.label}>{t('contact.situationLabel')}</label>
            <textarea
              style={s.textarea}
              placeholder={t('contact.situationPlaceholder')}
              value={form.situation}
              onChange={e => setForm(f => ({ ...f, situation: e.target.value }))}
              rows={6}
            />
          </div>

          <div style={s.checkboxRow}>
            <input
              type="checkbox"
              id="phoneCheck"
              checked={form.phonePreferred}
              onChange={e => setForm(f => ({ ...f, phonePreferred: e.target.checked }))}
              style={{ marginRight: '0.5rem' }}
            />
            <label htmlFor="phoneCheck" style={{ color: '#4a5568', fontSize: '0.95rem', cursor: 'pointer' }}>
              {t('contact.phonePreferredLabel')}
            </label>
          </div>

          {form.phonePreferred && (
            <div>
              <div style={s.field}>
                <label style={s.label}>{t('contact.phoneLabel')}</label>
                <PhoneInput
                  value={form.phoneNumber}
                  onChange={val => setForm(f => ({ ...f, phoneNumber: val }))}
                  style={s.input}
                />
              </div>
              <div style={s.field}>
                <label style={s.label}>{t('contact.preferredTimeLabel')}</label>
                <div style={s.timeRow}>
                  {TIME_OPTIONS.map(opt => (
                    <label key={opt.key} style={s.timeLabel}>
                      <input
                        type="checkbox"
                        checked={form.preferredTime.includes(opt.label)}
                        onChange={() => toggleTime(opt.label)}
                        style={{ marginRight: '0.4rem' }}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {error && <p style={{ color: '#e53e3e', fontSize: '0.9rem', marginBottom: '1rem' }}>{error}</p>}

          <button
            style={{ ...s.btnPrimary, opacity: sending ? 0.7 : 1 }}
            onClick={handleSend}
            disabled={sending}
          >
            {sending ? t('contact.sending') : (!user || !isClient) ? t('contact.registerAndSendBtn') : t('contact.sendBtn')}
          </button>

          {(!user || !isClient) && (
            <p style={{ color: '#718096', fontSize: '0.85rem', marginTop: '0.75rem' }}>
              {t('contact.registerNote')}
            </p>
          )}
        </div>
  );

  const infoBlock = (
        <div style={s.infoBox}>
          {attorney.profile_picture ? (
            <img
              src={`${SERVER_URL}${attorney.profile_picture}`}
              alt={attorney.full_name}
              style={s.infoAvatarImg}
            />
          ) : (
            <div style={s.infoAvatar}>&#9878;</div>
          )}
          <h2 style={s.infoName}>{attorney.full_name}</h2>
          {attorney.location && (
            <div style={s.infoRow}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#718096" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/>
              </svg>
              <span>{attorney.location}</span>
            </div>
          )}
          {attorney.practice_areas?.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <p style={s.infoSectionLabel}>{t('attorneyProfile.practiceAreas')}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {attorney.practice_areas.map(a => (
                  <span key={a} style={s.tag}>{translatePA(a, language)}</span>
                ))}
              </div>
            </div>
          )}
          {attorney.bio && (
            <div style={{ marginTop: '1rem' }}>
              <p style={s.infoSectionLabel}>{t('attorneyProfile.about')}</p>
              <p style={{ color: '#4a5568', fontSize: '0.9rem', lineHeight: '1.6' }}>{attorney.bio}</p>
            </div>
          )}
        </div>
  );

  return (
    <div style={{ ...s.page, ...(isMobile ? { padding: '1.25rem 1rem' } : {}) }}>
      <div style={{ ...s.layout, ...(isMobile ? s.layoutMobile : {}) }}>
        {isMobile ? (
          <>
            {infoBlock}
            {formBlock}
          </>
        ) : (
          <>
            {formBlock}
            {infoBlock}
          </>
        )}
      </div>
    </div>
  );
};

const s = {
  page: { backgroundColor: '#f7fafc', minHeight: 'calc(100vh - 120px)', padding: '3rem 2rem' },
  layout: { maxWidth: '1050px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 360px', gap: '2rem', alignItems: 'start' },
  layoutMobile: { display: 'flex', flexDirection: 'column', gap: '1.25rem' },
  formBox: { backgroundColor: 'white', borderRadius: '16px', padding: '2.5rem', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', boxSizing: 'border-box' },
  title: { fontSize: '1.6rem', fontWeight: '800', color: '#1a365d', marginBottom: '0.5rem' },
  note: { fontSize: '0.85rem', color: '#718096', marginBottom: '1.5rem' },
  salutation: { fontSize: '1rem', color: '#2d3748', fontStyle: 'italic', marginBottom: '1.25rem' },
  field: { marginBottom: '1.25rem' },
  label: { display: 'block', fontWeight: '600', color: '#4a5568', fontSize: '0.9rem', marginBottom: '0.4rem' },
  input: { width: '100%', padding: '0.75rem 1rem', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '0.75rem 1rem', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' },
  checkboxRow: { display: 'flex', alignItems: 'center', marginBottom: '1rem' },
  timeRow: { display: 'flex', gap: '1.5rem' },
  timeLabel: { display: 'flex', alignItems: 'center', color: '#4a5568', fontSize: '0.95rem', cursor: 'pointer' },
  btnPrimary: { backgroundColor: '#1a365d', color: 'white', border: 'none', padding: '0.9rem 2rem', borderRadius: '10px', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', width: '100%' },
  infoBox: { backgroundColor: 'white', borderRadius: '16px', padding: '2rem', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' },
  infoAvatar: { width: '72px', height: '72px', borderRadius: '50%', backgroundColor: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem', color: '#a0aec0', margin: '0 auto 1rem' },
  infoAvatarImg: { width: '72px', height: '72px', borderRadius: '50%', objectFit: 'cover', display: 'block', margin: '0 auto 1rem' },
  infoName: { fontSize: '1.2rem', fontWeight: '700', color: '#1a365d', textAlign: 'center', marginBottom: '0.5rem' },
  infoRow: { display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#718096', fontSize: '0.88rem', justifyContent: 'center', marginBottom: '0.25rem' },
  infoSectionLabel: { fontSize: '0.8rem', fontWeight: '700', color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' },
  tag: { backgroundColor: '#ebf8ff', color: '#2b6cb0', padding: '0.2rem 0.6rem', borderRadius: '10px', fontSize: '0.78rem' },
  center: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', color: '#718096' },
  sentBox: { maxWidth: '480px', margin: '6rem auto', textAlign: 'center', backgroundColor: 'white', padding: '3rem', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' },
};

export default ContactAttorney;
